import { bus } from '@skinsoft/bus';
import { DEFAULT_PROXY, IndexedDBCache } from '@skinsoft/data-store';
import { fetch } from '@skinsoft/network';
import _ from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, first, map, retry, switchMap, tap } from 'rxjs/operators';
import { CONDITION_REPORT_PDF_UPLOADED } from '../../../../apps/condition-report/src/busTopics';

export class MediaTransferHelper {

    public static getInstance() {
        if (!MediaTransferHelper.instance) {
            MediaTransferHelper.instance = new MediaTransferHelper();
        }

        return MediaTransferHelper.instance;
    }

    public static install(Vue) {
        Vue.prototype.$mediaTransferHelper = MediaTransferHelper.getInstance();
    }

    private static instance: MediaTransferHelper = null as any;

    private proxy = DEFAULT_PROXY;
    private cache = new IndexedDBCache();

    public downloadOriginalPDFFile(mediaId: string): Observable<any> {
        return this.proxy.fetch("/downloadMedias/originalFile",
            {
                "mediaId": mediaId,
            },
            'GET', undefined, undefined, true, true)
            .pipe(
                catchError((error) => of(undefined)),
                first())
    }

    public downloadMedia(mediaId: string, fileType = "vignette250", offlineLoading = false) {

        let media$: Observable<any>

        // If the mediaId is a localId, we look in cache
        if (mediaId.startsWith('local:')) {
            this.debugLog("Retrieving  " + mediaId + " from cache")

            media$ = this.cache.read(mediaId).pipe(
                switchMap(
                    (cacheValue) => {
                        if (cacheValue) {
                            let strCacheValue = cacheValue as unknown as string;
                            const fileName = mediaId.substring(mediaId.indexOf(':') + 1);
                            // If the cache value is a data url, we return it, the mediaId is not ready yet
                            if (strCacheValue.startsWith("data:")) {

                                if (!offlineLoading && window.navigator.onLine) {
                                    // Fetching the id of the media notice from the filename (TODO, handle doubles)
                                    this.proxy.fetch('/medias/fileName',
                                        undefined,
                                        'POST',
                                        { fileName },
                                        false,
                                        true
                                    ).subscribe(
                                        media => {
                                            const isTranscodingTerminated = _.get(media, 'associatedFileRecords.proxy')
                                            if (media.id && isTranscodingTerminated) {
                                                this.updateCacheTempKey(mediaId, media.id);
                                            }
                                        },
                                        () => this.repeatFindingMedia(fileName)
                                    )
                                }
                                return of({ dataUrl: strCacheValue, source: "cache" })
                            }
                            // If the cache value is not a data url, it is the new mediaId received from backend 
                            else {
                                // Fetching data from cache with the mediaId as key
                                return this.cache.read(strCacheValue).pipe(
                                    map(
                                        (secondCacheValue) => {
                                            // Deleting temporary cache entry
                                            this.cache.remove(mediaId)
                                            const strSecondCacheValue = secondCacheValue as unknown as string;
                                            // returning the data url and 'newMediaId' which can be used to update media identifiers 
                                            // in condition-report data (as in annotations for example)
                                            return { dataUrl: strSecondCacheValue, newMediaId: strCacheValue, source: "cache" }
                                        }
                                    )
                                )
                            }
                        } else {
                            // No data in cache
                            return of({});
                        }
                    }
                ),
            )
        } else {
            // If the mediaId is not local (in cache), retrieving media from network
            this.debugLog("Retrieving " + mediaId + " from network")
            media$ = this.proxy.fetch("/downloadMedias",
                {
                    "mediaId": mediaId,
                    // tslint:disable-next-line:object-literal-sort-keys
                    "fileType": fileType,
                },
                'GET', undefined, undefined, true)
                .pipe(
                    map(media => {
                        // If a media is retrieved from network, we delete the cache entry and return the dataUrl
                        if (media) {
                            this.cache.read(mediaId).pipe(first()).subscribe(cacheEntry => {
                                if (!!cacheEntry) {
                                    this.cache.remove(mediaId);
                                }
                            })
                            return { dataUrl: media, source: "network" }
                        }
                    }),
                    catchError(errors => {
                        // In case of error (typically thumbnail not ready yet), fallback on cached media if exists
                        this.debugLog("Media with id '" + mediaId + "' not available from network, falling back on cache")
                        return this.cache.read(mediaId).pipe(
                            first(),
                            map(entry => {
                                return { dataUrl: entry, source: "cache" }
                            })
                        )
                    },
                    ),
                );
        }
        return media$;
    }
    public repeatFindingMedia(fileName: string): void {
        this.proxy.fetch('/medias/fileName',
            undefined,
            'POST',
            { fileName },
            false,
            true
        ).subscribe(
                media => {
                    const isTranscodingTerminated = _.get(media, 'associatedFileRecords.proxy')
                    if (media.id) {
                        this.updateCacheTempKey('local:'+fileName, media.id);
                    }
                },
                () => setTimeout(() => this.repeatFindingMedia(fileName), 500)
            )
    }

    public findMediaIdFromFileName(fileName: string) {
        this.proxy.fetch('/medias/fileName',
            undefined,
            'POST',
            { fileName },
            false,
            true
        ).subscribe(
            media => {
                const isTranscodingTerminated = _.get(media, 'associatedFileRecords.proxy')
                if (media.id && isTranscodingTerminated) {
                    this.updateCacheTempKey('local:'+fileName, media.id);
                }
            },
            () =>  this.debugLog("Media with name " + fileName + " not found on backend")
        )
    }

    public uploadMedia(mimeType: string, file: File, localUrl: string, isAssociatedDocument?, isOffline?: boolean): string {

        // Setting image file in request body
        const formData = new FormData();
        const fileName =  "REP-"+Date.now()+"."+ file.name.split('.').pop()
        formData.append("files", file, fileName)

        // We set immediately the image in cache with a generated temp id
        // const tempKey = `local:${this.generateImageHash()}`
        const tempKey = `local:${fileName}`
        this.setImageInCache(tempKey, localUrl)
        if (!isOffline) {
            // Creating new empty media notice
            fetch.api(`/uploadMedias/filesGroup?mimeTypes=${mimeType}`, { method: 'PUT', body: formData }).pipe(
                switchMap(
                    groupId => {
                        return fetch.api(`/uploadMedias/beginIngest?groupId=${groupId}&isPrincipal=true&isAssociatedDocument=${!!isAssociatedDocument}`,
                            {
                                method: 'PUT',
                            })
                    }
                )
            ).subscribe(
                (response: any) => {
                    // When media is ready replace temp cache key by media notice id
                    this.debugLog("Set image in cache with key : " + response.mediaId)
                })
        }
        return tempKey;
    }

    public uploadMediaInCache(file: File, localUrl: string): string {

        // Setting image file in request body
        const formData = new FormData();
        formData.append("files", file)

        // We set immediately the image in cache with a generated temp id
        // const tempKey = `local:${this.generateImageHash()}`
        const tempKey = `local:${file.name}`
        this.setImageInCache(tempKey, localUrl)

        return tempKey;
    }


    public async uploadPDFDocument(mimeType: string, noticeId: string, file: File, isAssociatedDocument?) {
        // Setting image file in request body
        const formData = new FormData();
        formData.append("files", file)

        // Uploading image to backend
        const resp = await fetch.api(`/uploadMedias/filesGroup?mimeTypes=${mimeType}`, { method: 'PUT', body: formData }).pipe(
            switchMap(  
                groupId => {
                    return fetch.api(`/uploadMedias/beginIngest?groupId=${groupId}&noticeId=${noticeId}&noticeType=conditionReport&isPrincipal=true&isAssociatedDocument=${!!isAssociatedDocument}`,
                        {
                            method: 'PUT',
                        })
                }
            )
        ).subscribe(() => { 
            bus.emit(CONDITION_REPORT_PDF_UPLOADED) 
        })

    }

    public deleteImageInCache(id: string) {
        this.cache.remove(id)
    }

    private debugLog(message: any) {
        // tslint:disable-next-line:no-console
        console.debug("[Media-cache-management] " + JSON.stringify(message))
    }

    private setImageInCache(mediaId, localUrl) {
        this.cache.write(mediaId, localUrl)
    }

    /**
     * Changes the value of a key with another key,so that the next attempt
     * to access the first key data leads to use the new key
     * @param keyToReplace 
     * @param newKey 
     */
    private async updateCacheTempKey(keyToReplace, newKey) {
        this.cache.read(keyToReplace).pipe(first()).subscribe(
            cacheEntry => {
                if (!!cacheEntry) {
                    this.cache.write(keyToReplace, newKey);
                    this.cache.write(newKey, cacheEntry);
                    this.debugLog("Cache entry '" + keyToReplace + "' has been replaced with '" + newKey + "'")
                }
            }
        );
    }

    /**
     * Generates a random id of fixed length 
     * @param value 
     */
    private generateImageHash() {
        return '_' + Math.random().toString(36).substr(2, 9) + '-' + Math.random().toString(36).substr(2, 9)
    }
}